/**
 * EventBus 工具类
 * 用于组件间通信，实现发布-订阅模式
 */
window._duilie = []
class EventBus {
  constructor() {
    // 存储事件和对应的回调函数列表
    this.events = {};
  }

  /**
   * 订阅事件
   * @param {string} eventName - 事件名称
   * @param {Function} callback - 回调函数
   * @returns {Function} - 返回取消订阅的函数
   */
  on(eventName, callback) {
    if (!this.events[eventName]) {
      this.events[eventName] = [];
    }
    this.events[eventName].push(callback);

    // 返回取消订阅的函数，方便调用者使用
    return () => this.off(eventName, callback);
  }

  /**
   * 发布事件
   * @param {string} eventName - 事件名称
   * @param {...any} args - 传递给回调函数的参数
   */
  emit(eventName, ...args) {
    const eventCallbacks = this.events[eventName];
    if (eventCallbacks && eventCallbacks.length) {
      eventCallbacks.forEach(callback => {
        try {
          callback(...args);
        } catch (error) {
          console.error(`Error in event callback for '${eventName}':`, error);
        }
      });
    }
  }

  /**
   * 取消订阅事件
   * @param {string} eventName - 事件名称
   * @param {Function} callback - 要取消的回调函数
   */
  off(eventName, callback) {
    // 如果指定了事件名和回调函数，只移除该回调
    if (eventName && callback) {
      const eventCallbacks = this.events[eventName];
      if (eventCallbacks) {
        this.events[eventName] = eventCallbacks.filter(cb => cb !== callback);
      }
    }
    // 如果只指定了事件名，移除该事件的所有回调
    else if (eventName) {
      delete this.events[eventName];
    }
    // 如果都不指定，清空所有事件
    else {
      this.events = {};
    }
  }

  /**
   * 订阅一次事件，触发后自动取消订阅
   * @param {string} eventName - 事件名称
   * @param {Function} callback - 回调函数
   */
  once(eventName, callback) {
    const onceCallback = (...args) => {
      // 先取消订阅，确保只触发一次
      this.off(eventName, onceCallback);
      // 然后执行回调
      callback(...args);
    };
    this.on(eventName, onceCallback);
  }

  /**
   * 获取指定事件的订阅数量
   * @param {string} eventName - 事件名称
   * @returns {number} - 订阅数量
   */
  getSubscribersCount(eventName) {
    if (eventName) {
      return this.events[eventName] ? this.events[eventName].length : 0;
    }
    // 如果不指定事件名，返回所有事件的数量
    return Object.keys(this.events).length;
  }
}

// 创建并导出单例实例
const eventBus = new EventBus();

// 导出EventBus类和实例
export { EventBus };
export default eventBus;

/**
 * 使用示例：
 * 
 * 1. 在需要发布事件的组件中：
 * import eventBus from '@/utils/eventBus';
 * 
 * // 发布事件
 * eventBus.emit('message', 'Hello from Component A');
 * 
 * 2. 在需要订阅事件的组件中：
 * import eventBus from '@/utils/eventBus';
 * 
 * // 在组件挂载时订阅
 * onMounted(() => {
 *   // 订阅事件，接收取消订阅的函数
 *   const unsubscribe = eventBus.on('message', (data) => {
 *     console.log('Received message:', data);
 *   });
 *   
 *   // 存储取消订阅函数，在组件卸载时使用
 *   onUnmounted(() => {
 *     unsubscribe();
 *     // 或者直接使用 eventBus.off('message', callbackFn);
 *   });
 * });
 * 
 * 3. 使用once方法订阅一次性事件：
 * eventBus.once('dataLoaded', () => {
 *   console.log('This will be executed only once');
 * });
 */